import axios from "axios";
import { API_BASE_URL, getAuthToken } from "../artistStudio/shared";

// API helpers for the fan "Choose your plan" membership and its checkout.
// Prices, taxes, totals and renewal terms always come from the server; nothing here calculates money.
const url = (path) => `${API_BASE_URL}/v1/fan-membership/${path}`;
const config = (extra = {}) => ({ headers: { token: getAuthToken(), "Content-Type": "application/json" }, timeout: 20000, ...extra });
const result = (response) => response.data?.result;

export const fanMembershipApi = {
  // { billing_period: "monthly" | "annual" }
  plans: (billingPeriod) => axios.get(url("plans"), config({ params: billingPeriod ? { billing_period: billingPeriod } : {} })).then(result),
  // { plan_code | plan_id, billing_period } -> what the fan is about to buy; nothing is charged.
  preview: (body) => axios.post(url("checkout/preview"), body, config()).then(result),
  // Same body; returns { payment_intent_id, client_secret, ... }. The membership is still inactive.
  start: (body) => axios.post(url("checkout/start"), body, config()).then(result),
  // Test environments only (the server answers 404 elsewhere): { plan_code, billing_period, outcome: "success" | "declined" }
  simulate: (body) => axios.post(url("checkout/simulate"), body, config()).then(result),
  // { payment_intent_id } -> { status: "active" | "pending", message, subscription }; 402 if the payment failed.
  confirm: (body) => axios.post(url("checkout/confirm"), body, config({ timeout: 30000 })).then(result),
  current: () => axios.get(url("current"), config()).then(result),
  cancel: () => axios.post(url("cancel"), {}, config()).then(result),
  // { items: [{ id, date, plan_name, billing_period, billing_label, total_amount, currency, status }] }: paid memberships, newest first.
  history: () => axios.get(url("history"), config()).then(result),
};

// Purchase History: memberships and paid licences in one list, and the licence refund request behind its Refund column.
const root = (path) => `${API_BASE_URL}/v1/${path}`;
export const purchaseApi = {
  // { items: [{ key, type: "membership" | "licence", id, date, item, amount, currency, refund: { can_request, status, entitlement_id } }] }
  list: () => axios.get(root("purchase-history"), config()).then(result),
  // -> { eligible, code, message, amount_paid, currency, window_ends_at }
  refundEligibility: (entitlementId) => axios.get(root("licence-refund/eligibility"), config({ params: { entitlement_id: entitlementId } })).then(result),
  // { entitlement_id, reason (10 to 1000 characters) }
  refundRequest: (body) => axios.post(root("licence-refund/request"), body, config()).then(result),
};

// Preferences and saved cards (My Plan). get/save: { new_releases, licence_expiry_alerts, payment_receipts, marketing, public_profile,
// who_can_message: "everyone" | "following" | "none" }; save sends only what changed. cards: { cards: [{ id, brand, last4, exp_month,
// exp_year, renewing_licences }] }.
export const preferencesApi = {
  get: () => axios.get(root("fan-preferences"), config()).then(result),
  save: (changes) => axios.put(root("fan-preferences"), changes, config()).then(result),
  cards: () => axios.get(root("fan-payment-methods"), config()).then(result),
};

// The artist page a fan last looked at. The membership pages use it when the link did not carry ?artist=<slug>
// (for example the fan opened /fan-membership directly), so the purchase is still credited to that artist.
const LAST_ARTIST_KEY = "pipaan_last_artist_slug";
const ARTIST_SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,119}$/;
export const rememberArtist = (slug) => {
  const value = String(slug || "").trim().toLowerCase();
  if (!ARTIST_SLUG_PATTERN.test(value)) return;
  try { window.sessionStorage.setItem(LAST_ARTIST_KEY, value); } catch (e) { /* storage blocked: the ?artist= link still works */ }
};
export const recallArtist = () => {
  try {
    const value = window.sessionStorage.getItem(LAST_ARTIST_KEY) || "";
    return ARTIST_SLUG_PATTERN.test(value) ? value : "";
  } catch (e) { return ""; }
};

export const BILLING_PERIODS = ["monthly", "annual"];
export const isBillingPeriod = (value) => BILLING_PERIODS.includes(value);

export const money = (amount, currency = "USD") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
};

export const formatDate = (value) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const apiErrorMessage = (error, fallback = "Something went wrong. Please try again.") => error?.response?.data?.message || fallback;
export const apiErrorCode = (error) => error?.response?.data?.code || null;
